import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, query, orderBy, limit, onSnapshot } from 'firebase/firestore';

// Activity service for managing user activities across the application
class ActivityService {
  constructor() {
    this.activities = [];
    this.listeners = [];
    this.unsubscribeFirestore = null;
    this.initializeFirestoreListener();
  }

  async initializeFirestoreListener() {
    try {
      // Set up real-time listener for activities from Firestore
      const activitiesRef = collection(db, 'activities');
      const q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(50));
      
      this.unsubscribeFirestore = onSnapshot(q, (snapshot) => {
        const activities = [];
        snapshot.forEach((doc) => {
          activities.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        this.activities = activities;
        this.notifyListeners();
        console.log(`📊 Loaded ${activities.length} activities from Firestore`);
      }, (error) => {
        console.error('❌ Firestore listener error:', error);
        this.fallbackToLocalStorage();
      });
    } catch (error) {
      console.error('❌ Error setting up Firestore listener:', error);
      this.fallbackToLocalStorage();
    }
  }

  fallbackToLocalStorage() {
    console.log('🔄 Falling back to localStorage for activities');
    this.activities = this.getStoredActivities();
    this.notifyListeners();
  }

  getStoredActivities() {
    const stored = localStorage.getItem('recentActivities');
    return stored ? JSON.parse(stored) : [];
  }

  async saveToFirestore(activity) {
    try {
      const activitiesRef = collection(db, 'activities');
      const docRef = await addDoc(activitiesRef, {
        type: activity.type,
        description: activity.description,
        timestamp: activity.timestamp,
        user: activity.user,
        userRole: activity.userRole
      });
      console.log('✅ Activity saved to Firestore:', docRef.id);
    } catch (error) {
      console.error('❌ Error saving activity to Firestore:', error);
      // Fallback to localStorage
      this.activities.unshift(activity);
      this.activities = this.activities.slice(0, 50);
      this.saveToLocalStorage();
    }
  }

  saveToLocalStorage() {
    localStorage.setItem('recentActivities', JSON.stringify(this.activities.slice(0, 50)));
    this.notifyListeners();
  }

  async addActivity(type, description, userInfo = null) {
    const activity = {
      id: Date.now() + Math.random(),
      type,
      description,
      timestamp: new Date().toISOString(),
      user: userInfo?.name || userInfo?.email || 'System',
      userRole: userInfo?.role || 'system'
    };
    
    // Save to Firestore for real-time sharing across all sessions
    await this.saveToFirestore(activity);
    
    return activity;
  }

  getActivities() {
    return this.activities;
  }

  async clearActivities() {
    try {
      // Clear from Firestore would require admin privileges
      // For now, just clear local cache
      this.activities = [];
      this.notifyListeners();
    } catch (error) {
      console.error('Error clearing activities:', error);
    }
  }

  // Cleanup method to unsubscribe from Firestore
  cleanup() {
    if (this.unsubscribeFirestore) {
      this.unsubscribeFirestore();
    }
  }

  // Subscribe to activity updates
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.activities));
  }

  // Activity type constants
  static TYPES = {
    LOGIN: 'login',
    LOGOUT: 'logout', 
    PROVERB_ADD: 'proverb_add',
    PROVERB_EDIT: 'proverb_edit',
    PROVERB_DELETE: 'proverb_delete',
    USER_ADD: 'user_add',
    USER_EDIT: 'user_edit',
    USER_DELETE: 'user_delete',
    EXPORT: 'export',
    IMPORT: 'import',
    SYSTEM: 'system',
    DATABASE: 'database'
  };
}

// Global instance
export const activityService = new ActivityService();

// React hook for using activity service
export const useActivityService = () => {
  const { user } = useAuth();

  const addActivity = async (type, description) => {
    try {
      return await activityService.addActivity(type, description, user);
    } catch (error) {
      console.error('Error adding activity:', error);
      return null;
    }
  };

  return {
    addActivity,
    getActivities: () => activityService.getActivities(),
    clearActivities: () => activityService.clearActivities(),
    subscribe: (callback) => activityService.subscribe(callback),
    TYPES: ActivityService.TYPES
  };
};

export default activityService;
